import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from './portalAccess';

// The two gestures do not refuse the same things: opening refuses MODULE_NOT_DELIVERED
// and MARKET_RESTRICTION_INCOMPATIBLE, closing refuses neither, so a hand-written row
// stays erasable.
export type ModuleDirection = 'enable' | 'disable';

export const moduleRefusalOf = (error: unknown, direction: ModuleDirection): Refusal => {
    switch (refusalCode(error)) {
        case 'MODULE_NOT_DELIVERED':
            return {
                title: c('Account module refusal').t`This module has no screen yet.`,
                guidance: c('Account module refusal')
                    .t`It is named in the portal menu so that the referential is the menu, but opening it would open nothing: no reading path serves it. This is a state of the platform, not a right you are missing — it will become openable with the lot that ships its page, on every account at once. Nothing to do here, and clicking again will refuse again.`,
            };
        case 'MARKET_RESTRICTION_INCOMPATIBLE':
            return {
                title: c('Account module refusal').t`Some memberships of this account are restricted to a few markets.`,
                guidance: c('Account module refusal')
                    .t`This module cannot honour that restriction: nearly half the services and almost every need carry no market at all, so the lists would silently lose part of what they should show. Refusing is visible, serving half a list is not. Set those memberships back to « all markets », then open the module.`,
            };
        case 'MODULE_NOT_FOUND':
            return {
                title: c('Account module refusal').t`This module is not in the referential.`,
                guidance:
                    direction === 'enable'
                        ? c('Account module refusal')
                              .t`The referential has probably changed since this screen was loaded. Reload the page to see the modules as they are now.`
                        : c('Account module refusal')
                              .t`It cannot be closed, since nothing names it any more. Reload the page to see the modules as they are now.`,
            };
        case 'ORGANIZATION_NOT_FOUND':
            return {
                title: c('Account module refusal').t`This account no longer exists.`,
                guidance: c('Account module refusal')
                    .t`It has probably disappeared from the source since this screen was loaded. Reload the page to see the accounts as they are now.`,
            };
        case 'INTERNAL_ORGANIZATION_NOT_ALLOWED':
            return {
                title: c('Account module refusal').t`Freelance.com is not a client account.`,
                guidance: c('Account module refusal')
                    .t`The internal organisation carries no portal module: the back-office is a separate application, and it is not gated by them.`,
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Account module refusal').t`This account is outside your portfolio.`,
                guidance: c('Account module refusal')
                    .t`Deciding what a client account contains is reserved to whoever carries it. Ask an administrator whose portfolio holds this account.`,
            };
        case 'MISSING_PERMISSION':
            return {
                title: c('Account module refusal').t`Your role does not let you manage accounts.`,
                guidance: c('Account module refusal')
                    .t`A module is opened on an account, so it takes the same permission as opening that account to the portal — the screen cannot know beforehand whether you hold it, which is why the button was offered. Ask an administrator for that permission, or ask them to make the change.`,
            };
        case 'INTERNAL_STAFF_ONLY':
            return {
                title: c('Account module refusal').t`This gesture belongs to the back-office.`,
                guidance: c('Account module refusal')
                    .t`Deciding what a client account contains is a Freelance.com decision, so a client-side role cannot carry it whatever its permissions — otherwise an account would configure itself. The scope of the role is checked before the permission, which is why a custom role holding the right is refused too.`,
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Account module refusal').t`Your session no longer carries an active membership.`,
                guidance: c('Account module refusal').t`Sign in again, then start the change over.`,
            };
        default:
            return {
                title:
                    direction === 'enable'
                        ? c('Account module refusal').t`The server refused to open this module.`
                        : c('Account module refusal').t`The server refused to close this module.`,
                guidance: c('Account module refusal')
                    .t`This refusal is not one the screen knows how to read, so nothing here says what to do about it. The server's answer is below — it is what the support desk needs.`,
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};
